import { buildAgentDocumentEntries } from '../../../../shared/utils/regulation-document-rows.util';

export const REGULATIONS_AGENT_ID = 'regulations_agent';

/** Citation fields holding document file names / per-document metadata, folded into `documents[]`. */
const CITATION_DOCUMENT_FIELDS: readonly string[] = ['associated_documents', 'attachment_names', 'document_name'];
/** Citation fields that carry no meaning for the reader and must not surface as metadata rows. */
const CITATION_HIDDEN_FIELDS: readonly string[] = [
  'status',
  'accuracy',
  'insight_id',
  'insight_title',
  'insight_ref_urls',
  'insight_reference_links',
  'insight_markets',
  'reasoning',
  'contribution_level',
  'relevancy',
  'document_regulator_version',
  'document_version',
  'document_release_date',
  'insight_version',
];
const NOT_STATED_PATTERN = /^not\s+stated$/i;

function isNotStated(value: unknown): boolean {
  return typeof value === 'string' && NOT_STATED_PATTERN.test(value.trim());
}

function trimContributionLevel(value: unknown): string {
  return typeof value === 'string' ? value.trim().toUpperCase() : '';
}

function normalizeStringQuestionList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((entry: unknown): entry is string => typeof entry === 'string')
    .map((entry: string) => entry.trim())
    .filter((entry: string) => entry.length > 0);
}

function applyRelevanceScore(citation: Record<string, unknown>): void {
  if (citation['relevance_score']) {
    return;
  }
  const fromContribution = trimContributionLevel(citation['contribution_level']);
  const fromRelevancy = trimContributionLevel(citation['relevancy']);
  const fromBand = typeof citation['band'] === 'string' ? citation['band'].trim().toUpperCase() : '';
  const score = fromContribution || fromRelevancy || fromBand;
  if (score) {
    citation['relevance_score'] = score;
  }
}

export interface SavedAnswerMetadata {
  citations?: unknown;
  context?: unknown;
  active_identifiers?: unknown;
  follow_up_questions?: unknown;
}

export function isRegulationsAgent(agentId?: string): boolean {
  return agentId === REGULATIONS_AGENT_ID;
}

/**
 * Maps a regulations stream/save citation onto the source-panel / share-card shape.
 */
export function normalizeRegulationsCitation(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const citation = { ...(value as Record<string, unknown>) };
  const insightId = typeof citation['insight_id'] === 'string'
    ? citation['insight_id'].trim()
    : '';
  if (insightId) {
    citation['id'] = citation['id'] || insightId;
    citation['ins_id'] = citation['ins_id'] || insightId;
  }
  if (!citation['module_name'] && insightId) {
    citation['module_name'] = 'regulations';
  }

  const insightTitle = typeof citation['insight_title'] === 'string' ? citation['insight_title'].trim() : '';
  if (insightTitle && !citation['title']) {
    citation['title'] = insightTitle;
  }

  if (!citation['explanation'] && typeof citation['reasoning'] === 'string') {
    const reasoning = citation['reasoning'].trim();
    if (reasoning) {
      citation['explanation'] = reasoning;
    }
  }

  if (
    (!citation['countries'] || (Array.isArray(citation['countries']) && citation['countries'].length === 0)) &&
    Array.isArray(citation['insight_markets']) &&
    citation['insight_markets'].length > 0
  ) {
    citation['countries'] = citation['insight_markets'];
  }

  const documents = buildAgentDocumentEntries(
    citation['associated_documents'],
    citation['attachment_names'],
    citation['document_name']
  );
  const regulatorVersionRaw = citation['document_regulator_version'] ?? citation['document_version'];
  const regulatorVersion =
    typeof regulatorVersionRaw === 'string' && !isNotStated(regulatorVersionRaw)
      ? regulatorVersionRaw.trim()
      : '';
  const documentReleaseDateRaw = citation['document_release_date'];
  if (
    !citation['release_date'] &&
    typeof documentReleaseDateRaw === 'string' &&
    !isNotStated(documentReleaseDateRaw)
  ) {
    citation['release_date'] = documentReleaseDateRaw.trim();
  }
  const insightVersionRaw = citation['insight_version'];
  const insightVersion =
    typeof insightVersionRaw === 'string' && insightVersionRaw.trim().length > 0
      ? insightVersionRaw.trim()
      : '';
  if (insightVersion && !citation['version']) {
    citation['version'] = insightVersion;
  }
  const topLevelReleaseDate =
    typeof citation['release_date'] === 'string' ? citation['release_date'].trim() : '';
  if (documents.length > 0) {
    documents.forEach((doc) => {
      if (regulatorVersion && !doc.regulatory_version) {
        doc.regulatory_version = regulatorVersion;
      }
      if (topLevelReleaseDate && !doc.release_date) {
        doc.release_date = topLevelReleaseDate;
      }
    });
  }
  if (!citation['documents'] && documents.length > 0) {
    citation['documents'] = documents;
  }
  if (!citation['ref_url'] && Array.isArray(citation['insight_reference_links'])) {
    citation['ref_url'] = citation['insight_reference_links'];
  }
  if (!citation['ref_url'] && Array.isArray(citation['insight_ref_urls'])) {
    citation['ref_url'] = citation['insight_ref_urls'];
  }
  if (!citation['ref_url'] && Array.isArray(citation['ref_urls'])) {
    citation['ref_url'] = citation['ref_urls'];
  }
  delete citation['ref_urls'];
  CITATION_DOCUMENT_FIELDS.forEach((field: string) => delete citation[field]);
  applyRelevanceScore(citation);
  delete citation['band'];
  CITATION_HIDDEN_FIELDS.forEach((field: string) => delete citation[field]);

  return citation;
}

export interface RegulationsStreamMetadataSlice {
  citations?: Record<string, unknown>[];
  followUpQuestions?: string[];
}

/**
 * Normalizes regulations superagent / final-message metadata (citations, follow-ups).
 */
export function parseRegulationsStreamMetadata(metadata: unknown): RegulationsStreamMetadataSlice {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return {};
  }

  const record = metadata as Record<string, unknown>;
  const slice: RegulationsStreamMetadataSlice = {};

  if (Array.isArray(record['citations'])) {
    slice.citations = normalizeRegulationsCitationList(record['citations']);
  }

  const followUpKeys = ['follow_up_questions', 'follow_up', 'followups'] as const;
  for (const key of followUpKeys) {
    const questions = normalizeStringQuestionList(record[key]);
    if (questions.length > 0) {
      slice.followUpQuestions = questions;
      break;
    }
  }

  return slice;
}

export function normalizeRegulationsCitationList(citations: unknown): Record<string, unknown>[] {
  if (!Array.isArray(citations)) {
    return [];
  }
  return citations
    .map((citation: unknown) => normalizeRegulationsCitation(citation))
    .filter((citation: Record<string, unknown> | null): citation is Record<string, unknown> => citation !== null);
}

export function hasSourceReferences(references: unknown): boolean {
  if (Array.isArray(references)) {
    return references.length > 0;
  }
  if (references && typeof references === 'object') {
    return Object.keys(references as Record<string, unknown>).length > 0;
  }
  return false;
}

/**
 * Share carousel expects numbered keys (`1`, `2`, …), not a citations array.
 */
export function citationsToNumberedMetadata(citations: unknown): Record<string, Record<string, unknown>> {
  const numbered: Record<string, Record<string, unknown>> = {};
  normalizeRegulationsCitationList(citations).forEach((citation, index) => {
    numbered[String(index + 1)] = citation;
  });
  return numbered;
}

export function asSavedAnswerMetadata(value: unknown): SavedAnswerMetadata | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as SavedAnswerMetadata;
}
